import { useState, useRef } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";

interface ImageUploadProps {
  value: string;
  onChange: (url: string) => void;
}

export function ImageUpload({ value, onChange }: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/upload", formData);
      onChange(data.url);
    } catch (err: any) {
      alert(err.response?.data?.error || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleUpload(file);
        }}
      />

      {value ? (
        <div className="relative group">
          <img
            src={value}
            alt="Preview"
            className="w-full h-40 object-cover rounded-xl border border-white/10"
          />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 left-2 w-8 h-8 bg-red-500/80 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full h-32 border-2 border-dashed border-white/10 rounded-xl flex flex-col items-center justify-center gap-2 text-white/40 hover:border-[#66CDAA]/30 hover:text-[#66CDAA] transition-colors"
        >
          {uploading ? (
            <div className="w-6 h-6 border-2 border-[#66CDAA] border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Upload className="w-6 h-6" />
              <span className="text-sm">Upload image</span>
            </>
          )}
        </button>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Or paste image URL..."
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#66CDAA]/50 outline-none"
          dir="ltr"
        />
        {!value && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white/60 hover:text-white transition-colors"
          >
            <ImageIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
